/* eslint-disable @typescript-eslint/no-explicit-any */
import { useContext } from 'react';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { FEATURE_FLAGS, FLAG_TYPE } from '@marriott/mi-feature-flag-utils';

// Import the store and context
import {
  useSearchFilterStore,
  SearchFilterContextType,
  SearchFilterStore,
} from '../../modules/store/searchFilterZustandStore';
import { SearchFilterContext } from '../../organisms/OSearchResults/index.context';

/**
 * Hook that conditionally returns data from either the Zustand store or React Context
 * based on feature flags, maintaining the same API surface for seamless migration
 *
 * Uses feature flags: EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND and EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND
 *
 * Note: The Zustand store is updated proactively in searchResultsMainComponent where
 * the SearchFilterContext.Provider is set up, rather than reactively in this hook.
 * This is more efficient and follows the principle of updating at the source.
 */
export const useConditionalSearchFilterContext = (
  flagType: 'adf' | 'serp' = 'serp'
): SearchFilterContextType & Partial<Omit<SearchFilterStore, keyof SearchFilterContextType>> => {
  // Get feature flags from the client environment store
  const featureFlags = useClientEnvVarsStore(state => state.envVarsObject?.['featureFlags']) as unknown as Record<
    string,
    FLAG_TYPE
  >;

  // Determine which feature flag to check based on component type
  const shouldUseZustand =
    flagType === 'adf'
      ? featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND] ?? false
      : featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND] ?? false;

  // Always call hooks at top level (Rules of Hooks)
  const contextValue = useContext(SearchFilterContext);

  // Only subscribe to STATE using a selector (for reactivity)
  // Methods are stable references and should NOT be in a selector
  const searchQueryState = useSearchFilterStore(state => state.searchQueryState);

  // Get methods using selectors (methods are stable, won't cause re-renders)
  const setSearchQuery = useSearchFilterStore(state => state.setSearchQuery);
  const setQuery = useSearchFilterStore(state => state.setQuery);
  const setFilter = useSearchFilterStore(state => state.setFilter);
  const setApplyFilter = useSearchFilterStore(state => state.setApplyFilter);
  const updateOtherSearchQueryParam = useSearchFilterStore(state => state.updateOtherSearchQueryParam);
  const updatePoints = useSearchFilterStore(state => state.updatePoints);
  const updateTaxesAndAvailability = useSearchFilterStore(state => state.updateTaxesAndAvailability);
  const updateCurrency = useSearchFilterStore(state => state.updateCurrency);
  const updateAmenities = useSearchFilterStore(state => state.updateAmenities);
  const updateActivities = useSearchFilterStore(state => state.updateActivities);
  const updateBrands = useSearchFilterStore(state => state.updateBrands);
  const updateTransportationTypes = useSearchFilterStore(state => state.updateTransportationTypes);
  const updateQueryAndOtherSearchQueryParam = useSearchFilterStore(state => state.updateQueryAndOtherSearchQueryParam);
  const updateMedia = useSearchFilterStore(state => state.updateMedia);

  if (shouldUseZustand) {
    // Return Zustand store data with all direct methods
    return {
      searchQueryState, // Reactive state from selector
      setSearchQuery,
      setQuery,
      setFilter,
      setApplyFilter,
      updateOtherSearchQueryParam,
      updatePoints,
      updateTaxesAndAvailability,
      updateCurrency,
      updateAmenities,
      updateActivities,
      updateBrands,
      updateTransportationTypes,
      updateQueryAndOtherSearchQueryParam,
      updateMedia,
    };
  } else {
    // Return React Context data (only has searchQueryState and setSearchQuery)
    return contextValue;
  }
};

/**
 * Convenience hook specifically for components that should use the ADF feature flag
 * (AvailabilityCalendar and CalendarWrapper components)
 *
 * This hook checks EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND flag for SearchFilterContext
 */
export const useConditionalSearchFilterContextWithAdfFlag = (): SearchFilterContextType => {
  return useConditionalSearchFilterContext('adf');
};

/**
 * Convenience hook specifically for components that should use the SERP feature flag
 * (Most components in the SearchResults ecosystem)
 *
 * This hook checks EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND flag for SearchFilterContext
 */
export const useConditionalSearchFilterContextWithSerpFlag = (): SearchFilterContextType => {
  return useConditionalSearchFilterContext('serp');
};
